import { FC, useEffect, useRef, useState } from "react";
import { HeaderComponent } from "../../../components";
import { routes } from "../../../route/constants";
import {
    IonAccordion,
    IonAccordionGroup,
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonCol,
    IonContent,
    IonGrid,
    IonIcon,
    IonItem,
    IonPage,
    IonRow,
} from "@ionic/react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { watchInstance, watchServiceInstance } from "../../../services/watch";
import moment from "moment";
import {
    arrowDownCircle,
    arrowUpCircle,
    checkmarkCircle,
    closeCircle,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";

const MAX_MESSAGES = 200;

enum SendTestStatus {
    none = "none",
    pending = "pending",
    success = "success",
    fail = "fail",
}

interface DebugMessage {
    key: string;
    timestamp: string;
    messageId: string;
    id: string;
    direction: "incoming" | "outgoing";
    raw: any;
}

export const WatchDebug: FC<{}> = () => {
    const { t } = useTranslation();
    const watchStatus = useSelector((state: RootState) => state.watchStatus);
    const [messages, setMessages] = useState<DebugMessage[]>([]);
    const [sendTestStatus, setSendTestStatus] = useState<SendTestStatus>(
        SendTestStatus.none
    );
    const updateCountRef = useRef(0);
    const [updateCount, setUpdateCount] = useState(0);

    // Track watchStatus changes
    useEffect(() => {
        updateCountRef.current += 1;
        setUpdateCount(updateCountRef.current);
    }, [watchStatus]);

    // Subscribe to incoming and outgoing message streams once on mount
    useEffect(() => {
        const incomingSub = watchInstance
            ?.messageReceivedStream()
            .subscribe({
                next: (payload: any) => {
                    const newMessage: DebugMessage = {
                        key: `in-${payload.id}-${Date.now()}`,
                        timestamp: moment().format("LTS"),
                        messageId: payload.messageId,
                        id: payload.id,
                        direction: "incoming",
                        raw: payload,
                    };
                    setMessages((prev) =>
                        [newMessage, ...prev].slice(0, MAX_MESSAGES)
                    );
                },
                error: (error) =>
                    console.log(`[WatchDebug] Error: ${error}`),
                complete: () =>
                    console.log(
                        "[WatchDebug] Incoming stream completed"
                    ),
            });

        const responseSub = watchServiceInstance
            ?.responseSentStream()
            .subscribe({
                next: (payload: any) => {
                    const newMessage: DebugMessage = {
                        key: `out-${payload._id || payload.requestId}-${Date.now()}`,
                        timestamp: moment().format("LTS"),
                        messageId: payload.messageId,
                        id: payload._id || payload.requestId,
                        direction: "outgoing",
                        raw: payload,
                    };
                    setMessages((prev) =>
                        [newMessage, ...prev].slice(0, MAX_MESSAGES)
                    );
                },
                error: (error) =>
                    console.log(`[WatchDebug] Response error: ${error}`),
                complete: () =>
                    console.log(
                        "[WatchDebug] Response stream completed"
                    ),
            });

        return () => {
            incomingSub?.unsubscribe();
            responseSub?.unsubscribe();
        };
    }, []);

    const onTestClick = async () => {
        setSendTestStatus(SendTestStatus.pending);
        try {
            const status = await watchServiceInstance?.sendTestMessage();
            setSendTestStatus(SendTestStatus.success);
        } catch (error) {
            setSendTestStatus(SendTestStatus.fail);
        }
    };

    return (
        <IonPage>
            <HeaderComponent
                showBackButton
                defaultHref={routes.refsixMore}
                title={t("more.watchDebug.btWatchDebug")}
            />
            <IonContent>
                <IonCard>
                    <IonCardContent>
                        {t("more.watchDebug.btWatchDebugDescription")}
                    </IonCardContent>
                </IonCard>

                <IonCard>
                    <IonCardHeader>
                        <IonCardTitle>Watch Status</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <p style={{ marginBottom: "20px", color: "white" }}>
                            {t("more.watchDebug.updateCount")}: {updateCount}
                        </p>
                        <IonGrid>
                            {Object.keys(watchStatus).map((key: string) => {
                                return (
                                    <IonRow key={key}>
                                        <IonCol size="auto">{key}:</IonCol>
                                        <IonCol>
                                            {
                                                // @ts-ignore
                                                watchStatus[key] ? (
                                                    <IonIcon
                                                        icon={checkmarkCircle}
                                                        style={{
                                                            color: "green",
                                                        }}
                                                    ></IonIcon>
                                                ) : (
                                                    <IonIcon
                                                        icon={closeCircle}
                                                        style={{ color: "red" }}
                                                    ></IonIcon>
                                                )
                                            }
                                        </IonCol>
                                    </IonRow>
                                );
                            })}
                        </IonGrid>
                    </IonCardContent>
                </IonCard>
                <IonCard>
                    <IonCardHeader>
                        <IonCardTitle>
                            {t("more.watchDebug.messageReceivedLog")}
                        </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <IonAccordionGroup multiple={true}>
                            {messages.map((message) => {
                                return (
                                    <IonAccordion
                                        key={message.key}
                                        value={message.key}
                                    >
                                        <IonItem slot="header" color="light">
                                            <IonIcon
                                                icon={
                                                    message.direction ===
                                                    "incoming"
                                                        ? arrowDownCircle
                                                        : arrowUpCircle
                                                }
                                                style={{
                                                    color:
                                                        message.direction ===
                                                        "incoming"
                                                            ? "#4caf50"
                                                            : "#2196f3",
                                                    marginRight: "8px",
                                                    fontSize: "20px",
                                                }}
                                            />
                                            {message.timestamp} -{" "}
                                            {message.messageId}
                                        </IonItem>

                                        <pre
                                            style={{ fontSize: "10px" }}
                                            className="ion-padding"
                                            slot="content"
                                        >
                                            {JSON.stringify(
                                                message.raw,
                                                null,
                                                4
                                            )}
                                        </pre>
                                    </IonAccordion>
                                );
                            })}

                            {messages.length === 0 && (
                                <IonItem>
                                    {t("more.watchDebug.noMessagesReceived")}
                                </IonItem>
                            )}
                        </IonAccordionGroup>
                    </IonCardContent>
                </IonCard>
                <IonCard>
                    <IonCardHeader>
                        <IonCardTitle>
                            {t("more.watchDebug.sendMessage")}
                        </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <IonButton onClick={onTestClick}>
                            {t("more.watchDebug.sendTestMessage")}
                        </IonButton>
                        <p>
                            {t("more.watchDebug.status")}:{sendTestStatus}
                        </p>
                    </IonCardContent>
                </IonCard>
            </IonContent>
        </IonPage>
    );
};
